import {
  CircularProgress,
  DialogTitle,
  Divider,
  FormControl,
  InputAdornment,
  Stack,
} from "@mui/material";
import {
  CloseIconWrapper,
  CreateBoardWrapper,
  FontGrayNormal,
  ModalIconWrapper,
  ModalTitle,
  StyledContainedButton,
  StyledDialog,
  StyledFormControl,
  StyledInputLabel,
  StyledSelectField,
  StyledTextField,
} from "./style";
import {
  ArrowDownIcon,
  CloseIcon,
  FolderOpenIcon,
  ImageGalleryIcon,
} from "../../assets/Icons";
import { CreateBoardValueProps, ModalProps } from "../../utils/propTypes";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import {
  folderListsSelector,
  isLoadingSelector,
  openBoardModalSelector,
} from "../../store/metadata/selector";
import { folderItem } from "../../store/metadata/types";
import { useForm } from "react-hook-form";
import { createBoardValidationSchema } from "../../utils/formValidationSchema";
import { ErrorMessage } from "../../pageComponents/auth/style";
import { MetaDataSagaActions } from "../../store/metadata/sagas";
import { StyledDivider, StyledMenuItem } from "../../globalStyle";
import { MetaDataReducerAction } from "../../store/metadata/slice";

const CreateBoardModal = () => {
  const folderLists = useSelector(folderListsSelector);
  const {
    getValues,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<CreateBoardValueProps>({
    resolver: yupResolver(createBoardValidationSchema),
  });
  const dispatch = useDispatch();
  const loading = useSelector(isLoadingSelector);
  const openBoardModal = useSelector(openBoardModalSelector);

  const onSubmit = () => {
    const values = getValues();
    dispatch(MetaDataSagaActions.createBoard(values));
    reset();
  };

  const handleClose = () => {
    dispatch(MetaDataReducerAction.setOpenBoardModal(false))
  };

  return (
    <StyledDialog
      onClose={handleClose}
      open={openBoardModal}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ padding: "30px 30px 20px 30px" }}>
        <Stack direction="row" gap="0.75rem" alignItems="start">
          <ModalIconWrapper>
            <ImageGalleryIcon />
          </ModalIconWrapper>
          <Stack>
            <ModalTitle>Create Board</ModalTitle>
            <FontGrayNormal>Folders keep your boards organized</FontGrayNormal>
          </Stack>
        </Stack>
        <CloseIconWrapper aria-label="close" onClick={handleClose}>
          <CloseIcon />
        </CloseIconWrapper>
      </DialogTitle>
      <StyledDivider />
      <CreateBoardWrapper>
        <StyledFormControl fullWidth>
          <StyledInputLabel htmlFor="name">Board Name</StyledInputLabel>
          <StyledTextField
            id="name"
            variant="outlined"
            {...register("name", { required: true })}
          />
          {errors.name?.message && (
            <ErrorMessage>{errors.name?.message}</ErrorMessage>
          )}
        </StyledFormControl>

        <StyledFormControl fullWidth>
          <StyledInputLabel htmlFor="description">
            Description (Optional)
          </StyledInputLabel>
          <StyledTextField
            id="description"
            variant="outlined"
            required
            {...register("description")}
          />
        </StyledFormControl>

        <FormControl
          fullWidth
          sx={{
            "& > label": {
              top: "-22px",
              left: "-15px",
            },
          }}
        >
          <StyledInputLabel
            htmlFor="folderId"
            sx={{ fontSize: "1.5rem !important" }}
          >
            Location (Optional)
          </StyledInputLabel>
          <StyledSelectField
            id="folderId"
            variant="outlined"
            defaultValue=""
            startAdornment={
              <InputAdornment position="start">
                <FolderOpenIcon />
              </InputAdornment>
            }
            IconComponent={(props) => (
              <ArrowDownIcon className={props.className} />
            )}
            MenuProps={{
              PaperProps: {
                style: {
                  borderRadius: 15,
                },
              },
            }}
            {...register("folderId")}
          >
            {folderLists?.map((item: folderItem) => (
              <StyledMenuItem key={item._id} value={item._id}>
                {item.name}
              </StyledMenuItem>
            ))}
          </StyledSelectField>
        </FormControl>
      </CreateBoardWrapper>
      <StyledDivider />
      <StyledContainedButton
        variant="contained"
        size="small"
        sx={{
          margin: "1.875rem 1.875rem 1.875rem auto",
          padding: "0.313rem 2.375rem",
        }}
        onClick={handleSubmit(onSubmit)}
        disabled={loading}
        startIcon={loading && <CircularProgress size={20} color="inherit" />}
      >
        Create
      </StyledContainedButton>
    </StyledDialog>
  );
};

export default CreateBoardModal;
